import {
  FiChevronDown,
  FiLogOut,
  FiMoon,
  FiSearch,
  FiSettings,
  FiSun,
  FiUser,
  FiX,
} from "react-icons/fi";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "../../context/ThemeContext";

import {
  clearAuthSession,
} from "../../utils/authSession";

import {
  hasPermissionForUser,
  setCurrentUser,
  useCurrentUser,
} from "../../utils/permissions";

const pages = [
  {
    path: "/",
    title: "Dashboard",
    subtitle: "Dashboard / Overview",
    moduleKey: "dashboard",
    keywords: ["dashboard", "home", "overview"],
  },
  {
    path: "/containers",
    title: "Containers",
    subtitle: "Dashboard / Containers",
    moduleKey: "containers",
    keywords: [
      "containers",
      "container",
      "tracking",
      "booking",
      "bl",
      "vessel",
    ],
  },
  {
    path: "/tracking",
    title: "Live Tracking",
    subtitle: "Dashboard / Live Tracking",
    moduleKey: "tracking",
    keywords: [
      "live tracking",
      "tracking",
      "container tracking",
      "shipment tracking",
      "bl tracking",
      "booking tracking",
      "vessel tracking",
    ],
  },
  {
    path: "/track-shipment",
    title: "Track Shipment",
    subtitle: "Dashboard / Track Shipment",
    moduleKey: "tracking",
    keywords: [
      "track shipment",
      "shipment search",
      "container search",
      "container number",
      "bl number",
      "booking number",
      "vessel",
      "shipsgo request id",
    ],
  },
  {
    path: "/invoices",
    title: "Invoices",
    subtitle: "Dashboard / Invoices",
    moduleKey: "invoices",
    keywords: ["invoices", "invoice", "freight invoice", "bill"],
  },
  {
    path: "/invoices/create",
    title: "Create Invoice",
    subtitle: "Dashboard / Invoices / Create",
    moduleKey: "invoices",
    keywords: ["create invoice", "new invoice", "add invoice"],
  },
  {
    path: "/cost-price-list",
    title: "Freight Rates",
    subtitle: "Dashboard / Freight Rates",
    moduleKey: "cost-prices",
    keywords: [
      "freight",
      "rates",
      "cost",
      "cost price",
      "shipping rate",
    ],
  },
  {
    path: "/vendors",
    title: "Forwarders",
    subtitle: "Dashboard / Forwarders",
    moduleKey: "vendors",
    keywords: [
      "forwarders",
      "vendors",
      "carrier",
      "supplier",
      "shipping agent",
    ],
  },
  {
    path: "/payments",
    title: "Payments",
    subtitle: "Dashboard / Payments",
    moduleKey: "payments",
    keywords: ["payments", "payment", "paid", "bank transfer"],
  },
  {
    path: "/reports",
    title: "Reports",
    subtitle: "Dashboard / Reports",
    moduleKey: "reports",
    keywords: ["reports", "report", "analytics"],
  },
  {
    path: "/settings",
    title: "Settings",
    subtitle: "Dashboard / Settings",
    moduleKey: "settings",
    keywords: [
      "settings",
      "roles",
      "permissions",
      "users",
      "profile",
      "appearance",
      "theme",
    ],
  },
  {
    path: "/help",
    title: "Help & Support",
    subtitle: "Dashboard / Help & Support",
    moduleKey: "help",
    keywords: ["help", "support", "guide"],
  },
  {
    path: "/profile",
    title: "Profile",
    subtitle: "Dashboard / Profile",
    moduleKey: "profile",
    keywords: ["profile", "account", "my account"],
  },
];

const searchableModules = [
  {
    label: "Search Containers",
    path: "/containers",
    moduleKey: "containers",
    hint: "Container no, booking/BL no, vessel, origin, destination",
  },
  {
    label: "Search Live Tracking",
    path: "/tracking",
    moduleKey: "tracking",
    hint: "Container no, BL no, booking no, vessel",
  },
  {
    label: "Track a Shipment",
    path: "/track-shipment",
    moduleKey: "tracking",
    hint:
      "Open the shipment search page using a container, BL, booking, vessel, or request ID",
  },
  {
    label: "Search Invoices",
    path: "/invoices",
    moduleKey: "invoices",
    hint: "Invoice no, forwarder, supplier",
  },
  {
    label: "Search Freight Rates",
    path: "/cost-price-list",
    moduleKey: "cost-prices",
    hint: "Route, carrier, freight cost",
  },
  {
    label: "Search Forwarders",
    path: "/vendors",
    moduleKey: "vendors",
    hint: "Forwarder, carrier, contact, country",
  },
  {
    label: "Search Payments",
    path: "/payments",
    moduleKey: "payments",
    hint: "Reference, payee, method, type",
  },
];

const getPageMeta = (pathname) => {
  const cleanPath = pathname.replace(/\/+$/, "") || "/";

  if (
    cleanPath.startsWith("/invoices/") &&
    cleanPath.endsWith("/print")
  ) {
    return {
      title: "Invoice Print",
      subtitle: "Dashboard / Invoices / Print",
    };
  }

  return (
    pages.find((page) => page.path === cleanPath) || {
      title: "Page",
      subtitle: "Dashboard",
    }
  );
};

const getInitials = (name = "User") => {
  return String(name)
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
};

const formatRoleName = (role = "") => {
  return String(role)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
};

export default function Topbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, setTheme } = useTheme();

  const searchRef = useRef(null);
  const accountRef = useRef(null);

  const user = useCurrentUser();
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  useEffect(() => {
    setQuery("");
    setFocused(false);
    setAccountMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target)
      ) {
        setFocused(false);
      }

      if (
        accountRef.current &&
        !accountRef.current.contains(event.target)
      ) {
        setAccountMenuOpen(false);
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key !== "Escape") return;

      setFocused(false);
      setAccountMenuOpen(false);
      setShowLogoutModal(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscapeKey);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, []);

  const pageMeta = useMemo(
    () => getPageMeta(location.pathname),
    [location.pathname]
  );

  const accessiblePages = useMemo(
    () =>
      pages.filter((page) =>
        hasPermissionForUser(user, page.moduleKey, "read")
      ),
    [user]
  );

  const accessibleSearchModules = useMemo(
    () =>
      searchableModules.filter((module) =>
        hasPermissionForUser(user, module.moduleKey, "read")
      ),
    [user]
  );

  const pageResults = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    if (!normalizedQuery) {
      return accessiblePages.slice(0, 5);
    }

    return accessiblePages
      .filter((page) => {
        const haystack = [
          page.title,
          page.subtitle,
          ...(page.keywords || []),
        ]
          .join(" ")
          .toLowerCase();

        return haystack.includes(normalizedQuery);
      })
      .slice(0, 5);
  }, [query, accessiblePages]);

  const moduleSearchResults = useMemo(() => {
    const normalizedQuery = query.trim();

    if (!normalizedQuery) return [];

    return accessibleSearchModules.slice(0, 5).map((module) => ({
      ...module,
      searchPath: `${module.path}?search=${encodeURIComponent(
        normalizedQuery
      )}`,
    }));
  }, [query, accessibleSearchModules]);

  const showSearchDropdown =
    focused && Boolean(query.trim() || pageResults.length > 0);

  const canOpenProfile =
    hasPermissionForUser(
      user,
      "profile",
      "read"
    );
  const canOpenSettings =
    hasPermissionForUser(
      user,
      "settings",
      "read"
    );

  const displayRole =
    user?.roleName ||
    user?.assignedRole?.name ||
    user?.accessRole?.name ||
    formatRoleName(user?.role || "") ||
    "User";

  const goToPage = (path) => {
    navigate(path);
    setQuery("");
    setFocused(false);
    setAccountMenuOpen(false);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const normalizedQuery = query.trim();

    if (!normalizedQuery) return;

    if (pageResults.length > 0) {
      goToPage(pageResults[0].path);
      return;
    }

    if (moduleSearchResults.length > 0) {
      goToPage(moduleSearchResults[0].searchPath);
    }
  };

  const toggleTheme = () => {
    setTheme(theme === "dark" ? "light" : "dark");
  };

  const handleProfileButtonClick = () => {
    setFocused(false);
    setAccountMenuOpen((previous) => !previous);
  };

  const openLogoutConfirmation = () => {
    setAccountMenuOpen(false);
    setShowLogoutModal(true);
  };

  const handleLogout = () => {
    clearAuthSession();
    setCurrentUser(null);

    setAccountMenuOpen(false);
    setShowLogoutModal(false);

    navigate("/login", {
      replace: true,
    });
  };

  return (
    <>
      <header className="sticky top-0 z-40 flex h-[72px] items-center justify-between border-b border-slate-200 bg-white px-6 shadow-sm transition-colors duration-300 dark:border-slate-800 dark:bg-slate-900">
        <div className="flex min-w-0 flex-col justify-center">
          <h1 className="m-0 truncate p-0 text-[22px] font-semibold leading-[1.05] tracking-tight text-slate-800 dark:text-slate-100">
            {pageMeta.title}
          </h1>

          <p className="m-0 mt-1.5 truncate p-0 text-[13px] leading-[1.1] text-slate-400 dark:text-slate-500">
            {pageMeta.subtitle}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div ref={searchRef} className="relative">
            <form
              onSubmit={handleSubmit}
              className="flex h-11 w-[360px] items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 shadow-sm transition focus-within:border-blue-400 focus-within:bg-white dark:border-slate-700 dark:bg-slate-800 dark:focus-within:bg-slate-900"
            >
              <FiSearch className="shrink-0 text-[15px] text-slate-400" />

              <input
                type="text"
                value={query}
                onFocus={() => {
                  setFocused(true);
                  setAccountMenuOpen(false);
                }}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search pages, containers, invoices..."
                className="w-full bg-transparent text-[13px] text-slate-800 outline-none placeholder:text-slate-400 dark:text-slate-100"
              />

              {query ? (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  className="rounded-full p-1 text-slate-400 transition hover:bg-slate-200 hover:text-slate-700 dark:hover:bg-slate-700 dark:hover:text-white"
                  aria-label="Clear search"
                >
                  <FiX />
                </button>
              ) : null}
            </form>

            {showSearchDropdown ? (
              <div className="absolute right-0 top-[52px] z-[999] w-[420px] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900">
                <div className="max-h-[420px] overflow-y-auto p-2">
                  {pageResults.length > 0 ? (
                    <div className="p-2">
                      <p className="mb-2 px-2 text-[11px] font-bold uppercase tracking-wide text-slate-400">
                        Pages
                      </p>

                      <div className="space-y-1">
                        {pageResults.map((page) => (
                          <button
                            key={page.path}
                            type="button"
                            onClick={() => goToPage(page.path)}
                            className="w-full rounded-xl px-3 py-2 text-left transition hover:bg-slate-50 dark:hover:bg-slate-800"
                          >
                            <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                              {page.title}
                            </p>

                            <p className="mt-0.5 text-xs text-slate-400">
                              {page.subtitle}
                            </p>
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {moduleSearchResults.length > 0 ? (
                    <div className="border-t border-slate-100 p-2 dark:border-slate-800">
                      <p className="mb-2 px-2 text-[11px] font-bold uppercase tracking-wide text-slate-400">
                        Search Records
                      </p>

                      <div className="space-y-1">
                        {moduleSearchResults.map((item) => (
                          <button
                            key={item.path}
                            type="button"
                            onClick={() => goToPage(item.searchPath)}
                            className="w-full rounded-xl px-3 py-2 text-left transition hover:bg-slate-50 dark:hover:bg-slate-800"
                          >
                            <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                              {item.label} for “{query.trim()}”
                            </p>

                            <p className="mt-0.5 text-xs text-slate-400">
                              {item.hint}
                            </p>
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {pageResults.length === 0 &&
                  moduleSearchResults.length === 0 ? (
                    <div className="p-6 text-center text-sm text-slate-500 dark:text-slate-400">
                      No result found
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>

          <button
            type="button"
            onClick={toggleTheme}
            title={
              theme === "dark"
                ? "Switch to light mode"
                : "Switch to dark mode"
            }
            aria-label={
              theme === "dark"
                ? "Switch to light mode"
                : "Switch to dark mode"
            }
            className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-slate-200 bg-slate-50 text-lg text-slate-600 shadow-sm transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600 hover:ring-2 hover:ring-blue-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:border-blue-500 dark:hover:bg-blue-950 dark:hover:text-blue-300 dark:hover:ring-blue-900"
          >
            {theme === "dark" ? <FiMoon /> : <FiSun />}
          </button>

          <div ref={accountRef} className="relative">
            <button
              type="button"
              onClick={handleProfileButtonClick}
              aria-haspopup="menu"
              aria-expanded={accountMenuOpen}
              title="My Account"
              className={`flex h-11 items-center gap-1.5 rounded-full border px-1.5 shadow-sm transition ${
                accountMenuOpen
                  ? "border-blue-400 bg-blue-50 ring-2 ring-blue-100 dark:border-blue-500 dark:bg-blue-950 dark:ring-blue-900"
                  : "border-transparent hover:border-slate-200 hover:bg-slate-50 dark:hover:border-slate-700 dark:hover:bg-slate-800"
              }`}
            >
              <span className="grid h-9 w-9 place-items-center rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 text-sm font-bold text-white shadow-sm">
                {getInitials(user?.name || "User")}
              </span>

              <FiChevronDown
                className={`mr-1 text-sm text-slate-400 transition-transform duration-200 ${
                  accountMenuOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {accountMenuOpen ? (
              <div
                role="menu"
                className="absolute right-0 top-[52px] z-[1000] w-64 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900"
              >
                <div className="border-b border-slate-100 px-4 py-4 dark:border-slate-800">
                  <p className="text-sm font-bold text-slate-900 dark:text-slate-100">
                    My Account
                  </p>

                  <div className="mt-3 flex items-center gap-3">
                    <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 text-sm font-bold text-white">
                      {getInitials(user?.name || "User")}
                    </div>

                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {user?.name || "Admin"}
                      </p>

                      <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                        {user?.email || displayRole}
                      </p>

                      <p className="mt-0.5 truncate text-[11px] font-medium text-blue-600 dark:text-blue-400">
                        {displayRole}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-2">
                  <button
                    type="button"
                    role="menuitem"
                    disabled={!canOpenProfile}
                    onClick={() => {
                      if (canOpenProfile) {
                        goToPage("/profile");
                      }
                    }}
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-50 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-50 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-blue-300"
                  >
                    <span className="grid h-8 w-8 place-items-center rounded-lg bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-300">
                      <FiUser />
                    </span>

                    <span>Profile</span>
                  </button>

                  <button
                    type="button"
                    role="menuitem"
                    disabled={!canOpenSettings}
                    onClick={() => {
                      if (canOpenSettings) {
                        goToPage("/settings");
                      }
                    }}
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-50 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-50 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-blue-300"
                  >
                    <span className="grid h-8 w-8 place-items-center rounded-lg bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                      <FiSettings />
                    </span>

                    <span>Settings</span>
                  </button>
                </div>

                <div className="border-t border-slate-100 p-2 dark:border-slate-800">
                  <button
                    type="button"
                    role="menuitem"
                    onClick={openLogoutConfirmation}
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-medium text-slate-700 transition hover:bg-red-50 hover:text-red-600 dark:text-slate-300 dark:hover:bg-red-950/40 dark:hover:text-red-400"
                  >
                    <span className="grid h-8 w-8 place-items-center rounded-lg bg-red-50 text-red-600 dark:bg-red-950/50 dark:text-red-400">
                      <FiLogOut />
                    </span>

                    <span>Log out</span>
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </header>

      {showLogoutModal ? (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/65 p-4 backdrop-blur-md"
          role="dialog"
          aria-modal="true"
          aria-labelledby="topbar-logout-title"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setShowLogoutModal(false);
            }
          }}
        >
          <div className="w-full max-w-[390px] overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900">
            <div className="h-1.5 w-full bg-gradient-to-r from-blue-600 to-violet-600" />

            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-400">
                  <FiLogOut className="text-xl" />
                </div>

                <div>
                  <h2
                    id="topbar-logout-title"
                    className="text-xl font-bold text-slate-900 dark:text-slate-100"
                  >
                    Confirm Logout
                  </h2>

                  <p className="mt-1.5 text-sm leading-6 text-slate-500 dark:text-slate-400">
                    Are you sure you want to log out from your account?
                  </p>
                </div>
              </div>

              <div className="mt-7 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowLogoutModal(false)}
                  className="rounded-xl border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={handleLogout}
                  className="rounded-xl bg-red-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-red-600/20 transition hover:-translate-y-0.5 hover:bg-red-700"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}